(() => {
  const token = new URLSearchParams(location.hash.slice(1)).get('cdl-transfer');
  if (!token || !/^[A-Za-z0-9_-]{32}$/.test(token)) return;
  const root = window.Shopify?.routes?.root || '/';
  const panel = document.createElement('section');
  panel.className = 'cdl-transfer';
  panel.setAttribute('aria-label', 'Design added to cart');
  const style = document.createElement('style');
  style.textContent = `
    .cdl-transfer{position:fixed;inset:0;z-index:99999;overflow:auto;background:#f8f7f4;color:#252522;padding:clamp(24px,6vw,80px);display:flex;flex-direction:column;align-items:center;justify-content:safe center;font-family:var(--font-body--family,"Red Hat Text",sans-serif);font-weight:var(--font-body--weight,400)}
    .cdl-transfer *{box-sizing:border-box}
    .cdl-transfer__content{width:100%;max-width:880px;display:grid;grid-template-columns:minmax(0,1fr);gap:32px;align-items:center}
    .cdl-transfer__content:has(img){grid-template-columns:1fr 1fr;gap:56px}
    .cdl-transfer__image{width:100%;aspect-ratio:1;object-fit:contain;border-radius:16px;background:#fff}
    .cdl-transfer__body{width:100%;max-width:420px;margin:auto}
    .cdl-transfer h1{font-family:var(--font-heading--family,Newsreader,serif);font-weight:var(--font-heading--weight,200);font-size:clamp(30px,4vw,44px);line-height:1.15;margin:0 0 16px;text-wrap:balance}
    .cdl-transfer p{font-size:16px;line-height:1.6;margin:0 0 28px;color:#575750}
    .cdl-transfer__actions{display:flex;flex-direction:column;gap:12px}
    .cdl-transfer a,.cdl-transfer button{font:inherit;font-size:15px;min-height:48px;padding:14px 20px;border-radius:28px;text-align:center;text-decoration:none;cursor:pointer;display:block;line-height:1.4}
    .cdl-transfer__primary{background:#252522;color:#fff;border:1px solid #252522}
    .cdl-transfer__secondary{background:transparent;color:#252522;border:1px solid #b9b9af}
    .cdl-transfer__link{color:#575750;border:1px solid transparent;text-underline-offset:4px}
    .cdl-transfer a:hover,.cdl-transfer button:hover{filter:brightness(.85)}
    .cdl-transfer__link:hover{text-decoration:underline}
    .cdl-transfer a:focus-visible,.cdl-transfer button:focus-visible{outline:2px solid #67674f;outline-offset:4px}
    .cdl-transfer h1:focus{outline:none}
    .cdl-transfer ::selection{background:#e3dece;color:#252522}
    @media(max-width:600px){.cdl-transfer{padding:28px 24px}.cdl-transfer__content:has(img){grid-template-columns:1fr;gap:24px}.cdl-transfer__image{max-width:240px;margin:auto}.cdl-transfer h1{font-size:32px}.cdl-transfer__body{text-align:center}}
  `;
  panel.append(style);
  const content = document.createElement('div');
  content.className = 'cdl-transfer__content';
  const body = document.createElement('div');
  body.className = 'cdl-transfer__body';
  const heading = document.createElement('h1');
  heading.textContent = 'A little closer to yours.';
  const actions = document.createElement('div');
  actions.className = 'cdl-transfer__actions';
  content.append(body);
  panel.append(content);
  const message = document.createElement('p');
  message.textContent = 'Adding your design to your cart…';
  message.setAttribute('role', 'status');
  body.append(heading, message, actions);
  document.body.append(panel);

  async function cartRequest(path, body) {
    const response = await fetch(root + path, body ? { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) } : {});
    const data = await response.json();
    if (!response.ok) throw new Error(data.description || 'Your cart could not be updated. Please try again.');
    return data;
  }

  async function transfer() {
    const response = await fetch('https://studio.crystaldiylab.com/api/checkout/transfer/' + token, { credentials: 'omit', referrerPolicy: 'no-referrer' });
    if (!response.ok) throw new Error('This cart link has expired. Return to Studio and add your design again.');
    const data = await response.json();
    if (!Array.isArray(data.items) || data.items.length < 1 || data.items.length > 66 || !/^CDL-[A-Z0-9-]+$/.test(data.designId)) throw new Error('Invalid design transfer.');
    if (data.items.length === 1 && data.items[0].properties?._cdl_commerce_model !== 'single-product-v1') throw new Error('Incomplete design transfer.');
    const parentInput = data.items[0];
    const childInputs = data.items.slice(1);
    let cart = await cartRequest('cart.js');
    if (cart.currency !== 'USD') throw new Error('Please select the United States market before adding this design.');
    let parent = cart.items.find(item => item.properties?._cdl_design_id === data.designId);
    if (parent) {
      const children = cart.items.filter(item => item.parent_relationship?.parent_key === parent.key);
      const invalid = parent.quantity !== 1 || children.some(item => !childInputs.some(input => input.id === item.variant_id && input.quantity === item.quantity));
      if (invalid) {
        await cartRequest('cart/change.js', { id: parent.key, quantity: 0 });
        cart = await cartRequest('cart.js');
        parent = undefined;
      }
    }
    if (!parent) {
      const added = await cartRequest('cart/add.js', { items: [parentInput] });
      parent = added.items.find(item => item.properties?._cdl_design_id === data.designId);
      if (!parent) throw new Error('Unable to identify your design in the cart.');
    }
    const existing = cart.items.filter(item => item.parent_relationship?.parent_key === parent.key);
    const missing = childInputs.filter(input => !existing.some(item => item.variant_id === input.id && item.quantity === input.quantity));
    if (missing.length) {
      try {
        await cartRequest('cart/add.js', { items: missing.map(item => ({ ...item, parent_line_key: parent.key })) });
      } catch (error) {
        await cartRequest('cart/change.js', { id: parent.key, quantity: 0 });
        throw error;
      }
    }
    cart = await cartRequest('cart.js');
    parent = cart.items.find(item => item.properties?._cdl_design_id === data.designId);
    const children = cart.items.filter(item => item.parent_relationship?.parent_key === parent?.key);
    if (!parent || children.length !== childInputs.length || childInputs.some(input => !children.some(item => item.variant_id === input.id && item.quantity === input.quantity))) {
      if (parent) await cartRequest('cart/change.js', { id: parent.key, quantity: 0 });
      throw new Error('Your design is not complete in the cart. Please retry before checking out.');
    }
    await cartRequest('cart/update.js', { attributes: data.attributes });
    // Measurement must never prevent shopping; stable design IDs deduplicate retries.
    await fetch('https://studio.crystaldiylab.com/api/checkout/transfer/' + token, { method: 'POST', credentials: 'include', referrerPolicy: 'no-referrer' }).catch(() => {});
    history.replaceState(null, '', location.pathname + location.search);
    if (data.destination === 'checkout') {
      location.assign(root + 'checkout');
      return;
    }
    heading.textContent = 'Your design, saved in your cart.';
    message.textContent = 'Ready when you are. Review your cart or make room for another idea.';
    const preview = parent.properties?._cdl_preview_url;
    if (preview && /^https:\/\//i.test(preview)) {
      const image = document.createElement('img');
      image.className = 'cdl-transfer__image';
      image.alt = 'Your custom bracelet design';
      image.width = 400;
      image.height = 400;
      image.onerror = () => image.remove();
      image.src = preview;
      content.prepend(image);
    }
    const view = document.createElement('a');
    view.className = 'cdl-transfer__primary';
    view.href = root + 'cart';
    view.textContent = 'View cart';
    const design = document.createElement('a');
    design.className = 'cdl-transfer__secondary';
    design.href = 'https://studio.crystaldiylab.com/studio?new-design=1';
    design.textContent = 'Design another bracelet';
    const shop = document.createElement('a');
    shop.className = 'cdl-transfer__link';
    shop.href = root + 'collections/all';
    shop.textContent = 'Continue shopping';
    actions.append(view, design, shop);
    heading.tabIndex = -1;
    heading.focus();
  }
  transfer().catch(error => {
    heading.textContent = 'Let’s try that again.';
    message.textContent = error.message;
    const retry = document.createElement('button');
    retry.className = 'cdl-transfer__primary';
    retry.type = 'button';
    retry.textContent = 'Retry';
    retry.onclick = () => location.reload();
    const back = document.createElement('a');
    back.className = 'cdl-transfer__secondary';
    back.href = 'https://studio.crystaldiylab.com/';
    back.textContent = 'Return to Studio';
    actions.append(retry, back);
  });
})();
